import React, { memo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Typography as MuiTypography } from '@material-ui/core';

// styles
import useStyles from './styles';

const components = {
    // Headers
    h1: ({ children, ...props }) => (
        <MuiTypography variant="h4" component="h1" {...props}>
            {children}
        </MuiTypography>
    ),
    h2: ({ children, ...props }) => (
        <MuiTypography variant="h5" component="h2" {...props}>
            {children}
        </MuiTypography>
    ),
    h3: ({ children, ...props }) => (
        <MuiTypography variant="h6" component="h3" {...props}>
            {children}
        </MuiTypography>
    ),
    h4: ({ children, ...props }) => (
        <MuiTypography variant="body1" component="h4" style={{ fontWeight: 600 }} {...props}>
            {children}
        </MuiTypography>
    ),
    h5: ({ children, ...props }) => (
        <MuiTypography variant="body2" component="h5" style={{ fontWeight: 600 }} {...props}>
            {children}
        </MuiTypography>
    ),
    h6: ({ children, ...props }) => (
        <MuiTypography variant="caption" component="h6" style={{ fontWeight: 600 }} {...props}>
            {children}
        </MuiTypography>
    ),

    // Paragraphs
    p: ({ children, ...props }) => (
        <MuiTypography variant="body1" component="p" {...props}>
            {children}
        </MuiTypography>
    ),

    // Lists
    ol: ({ children, ...props }) => (
        <ol style={{ listStyleType: 'decimal', marginLeft: '1.5rem' }} {...props}>
            {children}
        </ol>
    ),
    ul: ({ children, ...props }) => (
        <ul style={{ listStyleType: 'disc', marginLeft: '1.5rem' }} {...props}>
            {children}
        </ul>
    ),
    li: ({ children, ...props }) => (
        <li style={{ paddingBottom: '0.25rem' }} {...props}>
            {children}
        </li>
    ),

    // Text formatting - inherit from parent context
    strong: ({ children, ...props }) => <strong {...props}>{children}</strong>,
    em: ({ children, ...props }) => <em {...props}>{children}</em>,

    // Links - use theme colors from parent context
    a: ({ href, children, ...props }) => (
        <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
            {children}
        </a>
    ),

    // Tables
    table: ({ children, ...props }) => (
        <table
            style={{
                width: '100%',
                borderCollapse: 'collapse',
                marginTop: '1rem',
                marginBottom: '1rem',
            }}
            {...props}
        >
            {children}
        </table>
    ),
    thead: ({ children, ...props }) => <thead {...props}>{children}</thead>,
    tbody: ({ children, ...props }) => <tbody {...props}>{children}</tbody>,
    tr: ({ children, ...props }) => <tr {...props}>{children}</tr>,
    td: ({ children, ...props }) => (
        <td
            style={{
                padding: '0.75rem',
                borderBottom: '1px solid rgba(27, 21, 74, 0.1)',
            }}
            {...props}
        >
            {children}
        </td>
    ),
    th: ({ children, ...props }) => (
        <th
            style={{
                padding: '0.75rem',
                borderBottom: '1px solid rgba(27, 21, 74, 0.1)',
                backgroundColor: 'rgba(27, 21, 74, 0.05)',
                fontWeight: 600,
                textAlign: 'left',
            }}
            {...props}
        >
            {children}
        </th>
    ),

    // Code
    code: ({ inline, children, ...props }) => {
        if (inline) {
            return (
                <code
                    style={{
                        backgroundColor: 'rgba(27, 21, 74, 0.05)',
                        padding: '0.25rem 0.5rem',
                        borderRadius: '0.25rem',
                        fontSize: '0.9em',
                        fontFamily: 'monospace',
                    }}
                    {...props}
                >
                    {children}
                </code>
            );
        }
        return (
            <code
                style={{
                    display: 'block',
                    backgroundColor: 'rgba(27, 21, 74, 0.05)',
                    padding: '1rem',
                    borderRadius: '0.5rem',
                    overflow: 'auto',
                    fontSize: '0.9em',
                    fontFamily: 'monospace',
                }}
                {...props}
            >
                {children}
            </code>
        );
    },

    // Pre (already handled by code block)
    pre: ({ children }) => <>{children}</>,

    // Blockquote
    blockquote: ({ children, ...props }) => (
        <blockquote
            style={{
                borderLeft: '4px solid #ED3378',
                paddingLeft: '1rem',
                marginLeft: 0,
                marginBottom: '1rem',
                fontStyle: 'italic',
                color: 'rgba(0, 0, 0, 0.6)',
            }}
            {...props}
        >
            {children}
        </blockquote>
    ),
};

const remarkPlugins = [remarkGfm];

const NonMemoizedMarkdown = ({ children, className = '' }) => {
    const classes = useStyles();

    return (
        <div className={`${classes.markdown} ${className}`}>
            <ReactMarkdown remarkPlugins={remarkPlugins} components={components}>
                {children}
            </ReactMarkdown>
        </div>
    );
};

export const Markdown = memo(
    NonMemoizedMarkdown,
    (prevProps, nextProps) => prevProps.children === nextProps.children && prevProps.className === nextProps.className
);

export default Markdown;
